import { getDateAsUtcNoonInISO } from '../../../../services/datetime-service';
import axios from 'axios';

import { transferOrderStatuses } from '../data/transfer-order-statuses';
import { getAbsoluteEachesByUomId, toBaseQuantity } from '../components/create-transfer-order-drawer/utils/uom-utils';

import type { CreateTransferOrderFormData } from '../components/create-transfer-order-drawer/types';
import type { TransferOrderStatus } from '../data/transfer-order-statuses';

type TransferOrderLineItem = {
  productId: number;
  originLocationId: number;
  quantity: number;
  productUoMId?: number;
  quantityInUom?: number;
  lotUuid?: string;
};

type CreateTransferOrderPayload = {
  originWarehouseId: number;
  destinationWarehouseId: number;
  orderDate: string;
  expectedDeliveryDate: string;
  referenceNumber: string;
  notes: string;
  products: TransferOrderLineItem[];
  status: TransferOrderStatus;
};

const buildLineItems = (formData: CreateTransferOrderFormData): TransferOrderLineItem[] => {
  const lineItems: TransferOrderLineItem[] = [];

  formData.products.forEach((product) => {
    product.locations.forEach((location) => {
      if (location.quantity > 0) {
        const isUomSelected = !!location.unitOfMeasureId && location.unitOfMeasureId !== 0;
        const absoluteEaches = getAbsoluteEachesByUomId(product.unitsOfMeasure, location.unitOfMeasureId);
        const baseQuantity = isUomSelected ? toBaseQuantity(location.quantity, absoluteEaches) : location.quantity;

        lineItems.push({
          productId: parseInt(product.productId, 10),
          originLocationId: parseInt(location.locationId, 10),
          quantity: baseQuantity,
          ...(isUomSelected && { productUoMId: location.unitOfMeasureId!, quantityInUom: location.quantity }),
          ...(location.lotUuid && { lotUuid: location.lotUuid }),
        });
      }
    });
  });

  return lineItems;
};

const buildPayload = (
  formData: CreateTransferOrderFormData,
  dateFormat: string,
  status: TransferOrderStatus,
): CreateTransferOrderPayload => ({
  originWarehouseId: parseInt(formData.originWarehouseId, 10),
  destinationWarehouseId: parseInt(formData.destinationWarehouseId, 10),
  orderDate: getDateAsUtcNoonInISO(formData.orderDate, dateFormat),
  expectedDeliveryDate: getDateAsUtcNoonInISO(formData.expectedDeliveryDate, dateFormat),
  referenceNumber: formData.referenceNumber,
  notes: formData.notes,
  status,
  products: buildLineItems(formData),
});

export const createTransferOrder = async (
  formData: CreateTransferOrderFormData,
  dateFormat: string,
  status: TransferOrderStatus = transferOrderStatuses.open,
  skipInventoryValidation = false,
): Promise<void> => {
  await axios.post('/transferOrder', buildPayload(formData, dateFormat, status), {
    params: skipInventoryValidation ? { skipInventoryValidation: true } : undefined,
  });
};

export const updateDraftTransferOrder = async (
  transferOrderId: number,
  formData: CreateTransferOrderFormData,
  dateFormat: string,
  status: TransferOrderStatus,
): Promise<void> => {
  await axios.put(`/transferOrder/${transferOrderId}`, buildPayload(formData, dateFormat, status));
};
